/**
 * Shared bottom-sheet picker used across the Food Pulse mobile app.
 *
 * Supports:
 *   • Static options (array of strings)
 *   • Async / dynamic options via ``fetchOptions(query)`` — the sheet
 *     debounces the search box and refetches when the user types.
 *   • Optional per-option subtitle (used e.g. by vendor picker to show
 *     the vendor's city under its name).
 *
 * Design matches the mockup: rounded-top sheet, sticky title bar with
 * a circular X close button, search input, then a divided list.
 */
import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

export default function BottomSheetPicker({
  open,
  title,
  options,          // array of strings OR array of {value,label,subtitle}
  fetchOptions,     // optional async (q) => [...]
  onSelect,         // (value, label) => void
  onClose,
  testIdPrefix = "sheet",
  searchable = true,
}) {
  const [q, setQ] = useState("");
  const [dynOpts, setDynOpts] = useState(null);
  const [loading, setLoading] = useState(false);

  // Normalise options into {value,label,subtitle}
  const normalised = useMemo(() => {
    const src = dynOpts !== null ? dynOpts : (options || []);
    return src.map((o) =>
      typeof o === "string" ? { value: o, label: o } : { value: o.value ?? o.label, label: o.label ?? o.value, subtitle: o.subtitle }
    );
  }, [options, dynOpts]);

  // Filter locally when using static options; delegate to fetchOptions otherwise.
  useEffect(() => {
    if (!open || !fetchOptions) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const opts = await fetchOptions(q);
        if (!cancelled) setDynOpts(opts || []);
      } catch {
        if (!cancelled) setDynOpts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open, fetchOptions]);

  // Reset query + dyn cache when the sheet re-opens
  useEffect(() => {
    if (open) { setQ(""); setDynOpts(fetchOptions ? null : null); }
  }, [open, fetchOptions]);

  const filtered = fetchOptions
    ? normalised
    : normalised.filter((o) => !q || o.label.toLowerCase().includes(q.toLowerCase()));

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end" data-testid={`${testIdPrefix}-sheet`}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-h-[85vh] bg-white rounded-t-3xl overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-white px-5 pt-4 pb-3 border-b z-10">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-bold">{title}</h2>
            <button
              onClick={onClose}
              aria-label="Close"
              data-testid={`${testIdPrefix}-close`}
              className="w-8 h-8 rounded-full border flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {searchable && (
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search"
              data-testid={`${testIdPrefix}-search`}
              className="mt-3 w-full border border-gray-200 rounded-lg px-3 py-2.5 text-base outline-none"
            />
          )}
        </div>
        {loading ? (
          <div className="p-6 text-center text-sm opacity-60">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-center text-sm opacity-60">No matches</div>
        ) : (
          <ul className="pb-6">
            {filtered.map((o, i) => (
              <li key={`${o.value}-${i}`}>
                <button
                  onClick={() => { onSelect(o.value, o.label); onClose(); }}
                  data-testid={`${testIdPrefix}-opt-${(o.value || "").toString().toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`}
                  className="w-full px-5 py-4 text-left border-b hover:bg-gray-50"
                >
                  <div className="text-base">{o.label}</div>
                  {o.subtitle && <div className="text-xs text-gray-500 mt-0.5">{o.subtitle}</div>}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
